import React, { useEffect, useState } from "react";
import Sidebar from "../Includes/Sidebar";
import GeneralService from "../../services/general.service";
import Footer from "../Includes/Footer";
import { useFormik } from "formik";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';

export default function EditStaff() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [existingImage, setExistingImage] = useState(null);
    const [removeExistingImageFlag, setRemoveExistingImageFlag] = useState(false);

    // Base URL for images
    const BASE_IMAGE_URL = process.env.REACT_APP_API_BASE_URL || 'https://azmrehabcentre.pk/api/public';

    const formik = useFormik({
        initialValues: {
            name: "",
            position: "",
            gender: "",
            phone: "",
            email: "",
            address: "",
            salary: "",
            image: null,
        },
validate: (values) => {
    const errors = {};
    
    // Name validation (required)
    if (!values.name || !values.name.trim()) {
        errors.name = "Name is required";
    } else if (values.name.length > 255) {
        errors.name = "Name must be less than 255 characters";
    }
    
    // Position validation (optional)
    if (values.position && values.position.length > 255) {
        errors.position = "Position must be less than 255 characters";
    }
    
    // Gender validation (required)
    if (!values.gender || !values.gender.trim()) {
        errors.gender = "Gender is required";
    } else if (!['male', 'female', 'other'].includes(values.gender)) {
        errors.gender = "Please select a valid gender";
    }
    
    // Phone validation (required)
    if (!values.phone || !values.phone.trim()) {
        errors.phone = "Phone is required";
    } else if (values.phone.length > 20) {
        errors.phone = "Phone must be less than 20 characters";
    }
    
    // Email validation (optional but must be valid if provided)
    if (values.email && values.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(values.email)) {
            errors.email = "Please enter a valid email address";
        } else if (values.email.length > 255) {
            errors.email = "Email must be less than 255 characters";
        }
    }
    
    // Address validation (required)
    if (!values.address || !values.address.trim()) {
        errors.address = "Address is required";
    } else if (values.address.length > 255) {
        errors.address = "Address must be less than 255 characters";
    }

    // FIXED: Better Salary validation
    if (values.salary !== null && values.salary !== undefined && values.salary !== '') {
        const salaryString = values.salary.toString().trim();
        if (salaryString) {
            const salaryValue = parseFloat(salaryString);
            if (isNaN(salaryValue)) {
                errors.salary = "Please enter a valid salary amount";
            } else if (salaryValue < 0) {
                errors.salary = "Salary cannot be negative";
            } else if (salaryValue > 99999999.99) {
                errors.salary = "Salary amount is too large";
            }
        }
    }
    
    // Image validation (only if new image is selected)
    if (values.image && typeof values.image === 'object') {
        const allowedTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
        const maxSize = 2048 * 1024; // 2MB in bytes
        
        if (!allowedTypes.includes(values.image.type)) {
            errors.image = "Only JPG, JPEG, PNG, and WEBP files are allowed";
        } else if (values.image.size > maxSize) {
            errors.image = "Image size must be less than 2MB";
        }
    }
    
    return errors;
},

// Updated onSubmit function
onSubmit: async (values) => {
    setSubmitting(true);

    try {
        console.log('=== FORM SUBMISSION DEBUG ===');
        console.log('Form values before submission:', values);
        console.log('Salary value specifically:', values.salary);
        console.log('Salary type:', typeof values.salary);
        console.log('Remove existing image flag:', removeExistingImageFlag);
        console.log('Has new image:', !!(values.image && typeof values.image === 'object'));
        console.log('Staff ID:', id);
        
        // FIXED: Better salary handling - don't convert to string prematurely
        let salaryValue = null;
        if (values.salary !== null && values.salary !== undefined && values.salary !== '') {
            const salaryString = values.salary.toString().trim();
            if (salaryString && salaryString !== '0') {
                const parsed = parseFloat(salaryString);
                if (!isNaN(parsed) && parsed >= 0) {
                    salaryValue = parsed;
                }
            }
        }
        
        console.log('Processed salary for submission:', salaryValue);
        
        // Prepare data object
        const staffData = {
            name: values.name.trim(),
            position: values.position ? values.position.trim() : '',
            gender: values.gender,
            phone: values.phone.trim(),
            email: values.email ? values.email.trim() : '',
            address: values.address.trim(),
            salary: salaryValue, // Use processed value (null or number)
            image: values.image, // File object or null
            removeExistingImage: removeExistingImageFlag
        };

        console.log('=== STAFF DATA TO SEND ===');
        console.log('staffData object:', {
            ...staffData,
            image: staffData.image ? `File: ${staffData.image.name} (${staffData.image.size} bytes)` : 'null'
        });

        // Add validation check
        if (!id) {
            throw new Error('Staff ID is missing');
        }

        // Call the update service
        console.log('Calling GeneralService.updateStaff...');
        const result = await GeneralService.updateStaff(id, staffData);
        
        console.log('=== UPDATE RESULT ===');
        console.log('Status:', result.status);
        console.log('Status Text:', result.statusText);
        console.log('Response Data:', result.data);
        
        // Check if the response indicates success
        if (result.status >= 200 && result.status < 300) {
            toast.success("✅ Staff updated successfully!");
            setTimeout(() => navigate("/view-staff"), 2000);
        } else {
            throw new Error(`Update failed with status: ${result.status}`);
        }
        
    } catch (error) {
        console.error("=== UPDATE ERROR ===");
        console.error("Error object:", error);
        console.error("Error response:", error.response?.data);
        
        let errorMessage = "❌ Failed to update staff. Please try again.";
        
        if (error.response?.data?.errors) {
            // Handle validation errors
            const validationErrors = error.response.data.errors;
            console.log('Validation errors:', validationErrors);
            const firstError = Object.values(validationErrors)[0];
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (error.message) {
            errorMessage = error.message;
        }
        
        toast.error(errorMessage);
    } finally {
        setSubmitting(false);
    }
},
    });

    useEffect(() => {
        const fetchData = async () => {
            if (!id) {
                toast.error("❌ Invalid staff ID");
                navigate("/view-staff");
                return;
            }

            setLoading(true);

            try {
                console.log("Fetching staff data for ID:", id);
                const response = await GeneralService.getStaffById(id);
                console.log("Staff data response:", response);
                
                // Handle different response structures
                const staffData = response.data?.data || response.data || response;
                
                if (!staffData) {
                    throw new Error("No staff data received");
                }

                // Set form values with existing data
                formik.setValues({
                    name: staffData.name || "",
                    position: staffData.position || "",
                    gender: staffData.gender || "",
                    phone: staffData.phone || "",
                    email: staffData.email || "",
                    address: staffData.address || "",
                    salary: staffData.salary || "",
                    image: null // Don't set existing image as file object
                });

                // Set existing image for preview
                if (staffData.image) {
                    const imageUrl = staffData.image.startsWith('http') 
                        ? staffData.image 
                        : `${BASE_IMAGE_URL}/${staffData.image.replace(/^\/+/, '')}`;
                    console.log('Setting existing image URL:', imageUrl);
                    setExistingImage(imageUrl);
                }
            } catch (error) {
                console.error("Error fetching staff data:", error);
                
                let errorMessage = "❌ Error fetching staff data. Please refresh the page.";
                
                if (error.response?.status === 404) {
                    errorMessage = "❌ Staff member not found.";
                    setTimeout(() => navigate("/view-staff"), 2000);
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, navigate]);

    // Enhanced image change handler with better validation
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        console.log('Image file selected:', file);
        
        if (file) {
            // Additional file validation
            const allowedTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
            const maxSize = 2048 * 1024; // 2MB
            
            console.log('File details:', {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: new Date(file.lastModified)
            });
            
            if (!allowedTypes.includes(file.type)) {
                toast.error("❌ Only JPG, JPEG, PNG, and WEBP files are allowed");
                e.target.value = ''; // Clear the input
                return;
            }
            
            if (file.size > maxSize) {
                toast.error("❌ Image size must be less than 2MB");
                e.target.value = ''; // Clear the input
                return;
            }
            
            formik.setFieldValue("image", file);
            
            // Create image preview
            const reader = new FileReader();
            reader.onloadend = () => {
                console.log('Image preview created');
                setImagePreview(reader.result);
            };
            reader.onerror = () => {
                console.error('Failed to read image file');
                toast.error("❌ Failed to read image file");
            };
            reader.readAsDataURL(file);
            
            // Reset remove existing image flag if user selects new image
            setRemoveExistingImageFlag(false);
        } else {
            console.log('No file selected or file cleared');
            formik.setFieldValue("image", null);
            setImagePreview(null);
        }
    };

    // Remove selected image
    const removeImage = () => {
        console.log('Removing selected image');
        formik.setFieldValue("image", null);
        setImagePreview(null);
        // Reset file input
        const fileInput = document.getElementById('image');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Remove existing image
    const removeExistingImage = () => {
        console.log('Removing existing image');
        setExistingImage(null);
        setRemoveExistingImageFlag(true);
    };

    // Network test function
    const testNetworkConnection = async () => {
        try {
            console.log('Testing network connection...');
            const response = await fetch(`${BASE_IMAGE_URL}/health-check`);
            console.log('Network test result:', response.status, response.statusText);
            return response.ok;
        } catch (error) {
            console.error('Network test failed:', error);
            return false;
        }
    };

    if (loading) {
        return (
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading staff data...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <>
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h6 className="mb-0">Edit Staff</h6>
                            <div>
                                <button 
                                    className="btn btn-info btn-sm me-2"
                                    onClick={testNetworkConnection}
                                    type="button"
                                    disabled={submitting}
                                >
                                    Test Connection
                                </button>
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => navigate("/view-staff")}
                                    type="button"
                                    disabled={submitting}
                                >
                                    Back to List
                                </button>
                            </div>
                        </div>

                        {/* Debug Info Panel (Remove in production) */}
                        <div className="alert alert-info text-start mb-4">
                            <small>
                                <strong>Debug Info:</strong><br/>
                                Staff ID: {id}<br/>
                                Has Existing Image: {existingImage ? 'Yes' : 'No'}<br/>
                                Has New Image: {formik.values.image ? 'Yes' : 'No'}<br/>
                                Remove Existing Image: {removeExistingImageFlag ? 'Yes' : 'No'}<br/>
                                Form Valid: {formik.isValid ? 'Yes' : 'No'}<br/>
                                Form Dirty: {formik.dirty ? 'Yes' : 'No'}
                            </small>
                        </div>

                        <form onSubmit={formik.handleSubmit}>
                            <div className="row">
                                {/* Name Field */}
                                <div className="col-md-6">
                                    <div className="form-floating mb-3">
                                        <input
                                            type="text"
                                            className={`form-control ${formik.touched.name && formik.errors.name ? 'is-invalid' : ''}`}
                                            id="name"
                                            name="name"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter staff name"
                                            required
                                        />
                                        <label htmlFor="name">Name *</label>
                                        {formik.touched.name && formik.errors.name && (
                                            <div className="invalid-feedback">{formik.errors.name}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Position Field */}
                                <div className="col-md-6">
                                    <div className="form-floating mb-3">
                                        <input
                                            type="text"
                                            className={`form-control ${formik.touched.position && formik.errors.position ? 'is-invalid' : ''}`}
                                            id="position"
                                            name="position"
                                            value={formik.values.position}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter position"
                                        />
                                        <label htmlFor="position">Position</label>
                                        {formik.touched.position && formik.errors.position && (
                                            <div className="invalid-feedback">{formik.errors.position}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Gender Field */}
                                <div className="col-md-6">
                                    <div className="form-floating mb-3">
                                        <select
                                            className={`form-control ${formik.touched.gender && formik.errors.gender ? 'is-invalid' : ''}`}
                                            id="gender"
                                            name="gender"
                                            value={formik.values.gender}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            required
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                        <label htmlFor="gender">Gender *</label>
                                        {formik.touched.gender && formik.errors.gender && (
                                            <div className="invalid-feedback">{formik.errors.gender}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Phone Field */}
                                <div className="col-md-6">
                                    <div className="form-floating mb-3">
                                        <input
                                            type="tel"
                                            className={`form-control ${formik.touched.phone && formik.errors.phone ? 'is-invalid' : ''}`}
                                            id="phone"
                                            name="phone"
                                            value={formik.values.phone}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter phone number"
                                            required
                                        />
                                        <label htmlFor="phone">Phone *</label>
                                        {formik.touched.phone && formik.errors.phone && (
                                            <div className="invalid-feedback">{formik.errors.phone}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Email Field */}
                                <div className="col-md-6">
                                    <div className="form-floating mb-3">
                                        <input
                                            type="email"
                                            className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                                            id="email"
                                            name="email"
                                            value={formik.values.email}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter email address"
                                        />
                                        <label htmlFor="email">Email</label>
                                        {formik.touched.email && formik.errors.email && (
                                            <div className="invalid-feedback">{formik.errors.email}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Salary Field */}
                                <div className="col-md-6">
                                    <div className="form-floating mb-3">
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="99999999.99"
                                            className={`form-control ${formik.touched.salary && formik.errors.salary ? 'is-invalid' : ''}`}
                                            id="salary"
                                            name="salary"
                                            value={formik.values.salary}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter salary amount"
                                        />
                                        <label htmlFor="salary">Salary</label>
                                        {formik.touched.salary && formik.errors.salary && (
                                            <div className="invalid-feedback">{formik.errors.salary}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Address Field */}
                                <div className="col-md-12">
                                    <div className="form-floating mb-3">
                                        <textarea
                                            className={`form-control ${formik.touched.address && formik.errors.address ? 'is-invalid' : ''}`}
                                            id="address"
                                            name="address"
                                            value={formik.values.address}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter address"
                                            style={{ height: '100px' }}
                                            required
                                        />
                                        <label htmlFor="address">Address *</label>
                                        {formik.touched.address && formik.errors.address && (
                                            <div className="invalid-feedback">{formik.errors.address}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Image Upload Field */}
                                <div className="col-md-12">
                                    <div className="mb-3">
                                        <label htmlFor="image" className="form-label">Staff Image</label>
                                        <input
                                            type="file"
                                            className={`form-control ${formik.touched.image && formik.errors.image ? 'is-invalid' : ''}`}
                                            id="image"
                                            name="image"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            onChange={handleImageChange}
                                        />
                                        <div className="form-text">Max size: 2MB. Allowed formats: JPG, JPEG, PNG, WEBP</div>
                                        {formik.touched.image && formik.errors.image && (
                                            <div className="invalid-feedback">{formik.errors.image}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Existing Image Preview */}
                                {existingImage && !imagePreview && (
                                    <div className="col-md-12">
                                        <div className="mb-3">
                                            <label className="form-label">Current Image</label>
                                            <div className="image-preview-container">
                                                <img
                                                    src={existingImage}
                                                    alt="Current Staff"
                                                    className="image-preview"
                                                    onError={(e) => {
                                                        console.error('Image failed to load:', existingImage);
                                                        e.target.style.display = 'none';
                                                        setExistingImage(null);
                                                        toast.warning('⚠️ Current image could not be loaded');
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger remove-image-btn"
                                                    onClick={removeExistingImage}
                                                    title="Remove current image"
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* New Image Preview */}
                                {imagePreview && (
                                    <div className="col-md-12">
                                        <div className="mb-3">
                                            <label className="form-label">New Image Preview</label>
                                            <div className="image-preview-container">
                                                <img
                                                    src={imagePreview}
                                                    alt="New Staff Preview"
                                                    className="image-preview"
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger remove-image-btn"
                                                    onClick={removeImage}
                                                >
                                                    <i className="fas fa-times"></i> Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Submit Button */}
                                <div className="col-md-12 d-flex">
                                    <button
                                        className="btn btn-submit"
                                        type="submit"
                                        disabled={submitting || !formik.isValid}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            "Update Staff"
                                        )}
                                    </button>
                                    
                                    {/* Reset Button */}
                                    <button
                                        className="btn btn-secondary ms-3"
                                        type="button"
                                        onClick={() => {
                                            formik.resetForm();
                                            setImagePreview(null);
                                            setRemoveExistingImageFlag(false);
                                            const fileInput = document.getElementById('image');
                                            if (fileInput) fileInput.value = '';
                                        }}
                                        disabled={submitting}
                                    >
                                        Reset Form
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <Footer />
            </div>

            <style jsx>{`
                .btn-submit {
                    background-color: #f44c00;
                    border-color: #f44c00;
                    padding: 14px 28px;
                    font-size: 16px;
                    font-weight: 600;
                    text-transform: uppercase;
                    border-radius: 30px;
                    transition: background-color 0.3s, border-color 0.3s;
                    display: inline-block;
                    margin-top: 10px;
                    color: #fff;
                }

                .btn-submit:hover:not(:disabled) {
                    background-color: #d43f00;
                    border-color: #d43f00;
                    color: #fff;
                }

                .btn-submit:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-submit:focus {
                    outline: none;
                    box-shadow: 0 0 0 0.2rem rgba(244, 76, 0, 0.25);
                }

                .image-preview-container {
                    position: relative;
                    display: inline-block;
                }

                .image-preview {
                    max-width: 200px;
                    max-height: 200px;
                    border-radius: 8px;
                    border: 2px solid #dee2e6;
                    object-fit: cover;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                .remove-image-btn {
                    position: absolute;
                    top: -10px;
                    right: -10px;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                }

                .invalid-feedback {
                    display: block;
                }
            `}</style>
        </>
    );
}